"use client";

import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
const styleTags = ["Modern Minimalist (Urban Chic)", "Bohemian Spirit (Free Flow)", "Classic Tailored (Timeless)", "Streetwear Edge (Bold)", "Evening Elegance (Sophisticated)", "Casual Comfort (Everyday)", "Avant-Garde (Unique)", "Sustainable Luxury (Eco-Conscious)"];
function ProductsCarouselConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const [currentIndex, setCurrentIndex] = useState(0);
  const nextSlide = useCallback(() => {
    if (!products) return;
    setCurrentIndex(prev => (prev + 1) % products.length);
  }, [products]);
  const prevSlide = useCallback(() => {
    if (!products) return;
    setCurrentIndex(prev => (prev - 1 + products.length) % products.length);
  }, [products]);
  if (loading) {
    return <div className="w-full aspect-[16/9] bg-secondary animate-pulse rounded-lg flex items-center justify-center">
        <span className="text-muted-foreground font-default text-xs tracking-widest uppercase">Loading Collection...</span>
      </div>;
  }
  if (error || !products || products.length === 0) {
    return <div className="py-12 text-center border border-dashed border-border rounded-lg">
        <p className="text-sm font-default tracking-wider uppercase text-muted-foreground">
          {error ? "Unable to load arrivals." : "No items in collection."}
        </p>
      </div>;
  }
  const currentProduct = products[currentIndex];
  const currentStyle = styleTags[currentIndex % styleTags.length];
  return <div className="relative w-full bg-secondary overflow-hidden rounded-lg">
      <div className="container mx-auto px-0">
        <div className="flex flex-col md:flex-row min-h-[600px]">
          {}
          <div className="w-full md:w-1/2 relative overflow-hidden bg-muted">
            <AnimatePresence mode="wait">
              <motion.img key={currentProduct.id} src={currentProduct.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/placeholder.png"} alt={currentProduct.title} initial={{
              opacity: 0,
              scale: 1.1
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1]
            }} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </AnimatePresence>
          </div>

          {}
          <div className="w-full md:w-1/2 p-8 md:p-16 lg:p-24 flex flex-col justify-center bg-secondary">
            <AnimatePresence mode="wait">
              <motion.div key={currentProduct.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.5,
              ease: "easeOut"
            }} className="space-y-6">
                <span className="inline-block font-default text-xs tracking-[0.2em] uppercase text-accent font-semibold">
                  {currentStyle}
                </span>

                <motion.h2 initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.2,
                duration: 0.6
              }} className="font-default text-foreground text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tighter">
                  {currentProduct.title}
                </motion.h2>

                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-muted-foreground font-default">$</span>
                  <span className="text-3xl font-default font-semibold text-foreground tabular-nums">
                    {Number(currentProduct.price ?? 0).toFixed(2)}
                  </span>
                </div>

                <div className="pt-8 flex flex-wrap gap-4 items-center">
                  <AddToCartButton productId={currentProduct.id} variant="default" size="lg" buttonClassName="bg-primary text-primary-foreground px-8 py-6 text-sm uppercase tracking-widest hover:bg-primary/90 transition-all duration-300 rounded-lg" text="Add to Atelier" showQuantity={false} />
                  
                  <Link to={currentProduct.link} className="group flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-foreground hover:text-accent transition-colors">
                    View Details
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>

            {}
            <div className="mt-16 flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={prevSlide} className="size-12 rounded-lg bg-primary border-primary text-primary-foreground hover:bg-accent hover:border-accent transition-all duration-300">
                <ChevronLeft className="size-6" strokeWidth={1.25} />
              </Button>
              <Button variant="outline" size="icon" onClick={nextSlide} className="size-12 rounded-lg bg-primary border-primary text-primary-foreground hover:bg-accent hover:border-accent transition-all duration-300">
                <ChevronRight className="size-6" strokeWidth={1.25} />
              </Button>
              
              <div className="ml-4 flex gap-1">
                {products.map((_, i) => <div key={i} data-index={i} className={`h-1 transition-all duration-300 rounded-lg ${i === currentIndex ? "w-8 bg-accent" : "w-2 bg-border"}`} />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function FeaturedCollection() {
  const wp_query_params = {
    per_page: 8,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2035" id="featuredcollection" className="relative bg-background text-foreground py-24 md:py-36 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-[1440px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 md:mb-20">
          <div className="lg:col-span-9">
            <div className="flex items-center gap-3 mb-8">
              <span className="font-default text-xs tracking-[0.18em] uppercase text-accent font-semibold">
                § 01 — New Arrivals
              </span>
              <span aria-hidden className="inline-block size-1.5 bg-accent" />
            </div>

            <h1 className="font-default text-foreground text-[clamp(2.25rem,4.8vw,4.5rem)] font-bold leading-[1.02] tracking-tighter">
              Curated for the
              <br />
              <span className="text-accent">modern wardrobe</span>
            </h1>
          </div>

          <div className="lg:col-span-3 flex lg:justify-end">
            <Link to="/ShopPage" className="group inline-flex items-center gap-3 font-default text-xs tracking-[0.16em] uppercase text-foreground hover:text-accent transition-colors duration-200">
              <span className="border-b border-foreground group-hover:border-accent pb-1 transition-colors duration-200">
                Explore Collection
              </span>
              <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        order: "desc",
        orderby: "date"
      }}>
          <ProductsCarouselConsumer />
        </EcommerceProductsProvider>

        <div className="mt-12 flex items-center gap-4 font-default text-[11px] tracking-[0.16em] uppercase text-muted-foreground">
          <span className="h-px w-12 bg-border" />
          <span>Lush Fashion — Seasonal Edit</span>
          <span aria-hidden className="inline-block size-1.5 bg-accent" />
        </div>
      </div>
    </section>;
}